import React from 'react';

const FORGE_EXTENSION_CONTAINER_SELECTOR = '[data-forge-extension-container]';
const MIN_TABLE_COLUMN_WIDTH_WITH_EXTENSION = 160;
// Ignore subpixel differences from zoom and layout rounding to avoid ResizeObserver churn.
const CONTAINER_WIDTH_TOLERANCE_PX = 1;

export const NESTED_EXTENSION_TABLE_INLINE_START_OFFSET = 32;

export type AdfNode = {
	attrs?: Record<string, unknown>;
	content?: AdfNode[];
	type?: string;
};

type TableAnalysis = {
	containsExtension: boolean;
	nestedExtensionTableMinWidth?: number;
};

type ForgeExtensionContainerWidthResult = {
	extensionContainerWidth: number | undefined;
	rendererContainerRef: React.RefObject<HTMLDivElement>;
};

const isExtensionNode = (node: AdfNode): boolean =>
	node.type === 'extension' || node.type === 'bodiedExtension' || node.type === 'inlineExtension';

const getTableColumnCount = (tableNode: AdfNode): number => {
	if (!Array.isArray(tableNode.content)) {
		return 0;
	}

	return tableNode.content.reduce((maxColumns, row) => {
		if (row.type !== 'tableRow' || !Array.isArray(row.content)) {
			return maxColumns;
		}

		const columns = row.content.reduce((count, cell) => {
			const colspan = typeof cell.attrs?.colspan === 'number' ? cell.attrs.colspan : 1;
			return count + colspan;
		}, 0);

		return Math.max(maxColumns, columns);
	}, 0);
};

export const getNestedExtensionTableAnalysis = (node: unknown): TableAnalysis => {
	if (!node || typeof node !== 'object') {
		return {
			containsExtension: false,
		};
	}

	const adfNode = node as AdfNode;
	const childAnalyses = adfNode.content?.map(getNestedExtensionTableAnalysis) ?? [];
	const containsExtension =
		isExtensionNode(adfNode) || childAnalyses.some((analysis) => analysis.containsExtension);
	const childMinWidth = Math.max(
		0,
		...childAnalyses.map((analysis) => analysis.nestedExtensionTableMinWidth ?? 0),
	);

	if (adfNode.type !== 'table' || !containsExtension) {
		return {
			containsExtension,
			nestedExtensionTableMinWidth: childMinWidth || undefined,
		};
	}

	const columnCount = getTableColumnCount(adfNode);
	const currentWidth = typeof adfNode.attrs?.width === 'number' ? adfNode.attrs.width : 0;
	const minWidth = columnCount * MIN_TABLE_COLUMN_WIDTH_WITH_EXTENSION;

	return {
		containsExtension,
		nestedExtensionTableMinWidth: Math.max(childMinWidth, currentWidth, minWidth) || undefined,
	};
};

const resizeNestedExtensionTablesInternal = (
	node: AdfNode,
	tableWidth: number,
): { containsExtension: boolean; node: AdfNode } => {
	const resizedChildren = node.content?.map((child) =>
		resizeNestedExtensionTablesInternal(child, tableWidth),
	);
	const containsExtension =
		isExtensionNode(node) || Boolean(resizedChildren?.some((child) => child.containsExtension));
	const resizedContent = resizedChildren?.map((child) => child.node);
	const resizedNode = resizedContent ? { ...node, content: resizedContent } : node;

	if (node.type !== 'table' || !containsExtension) {
		return { containsExtension, node: resizedNode };
	}

	const currentWidth = typeof node.attrs?.width === 'number' ? node.attrs.width : 0;

	return {
		containsExtension,
		node: {
			...resizedNode,
			attrs: {
				...node.attrs,
				width: Math.max(currentWidth, tableWidth),
			},
		},
	};
};

export const resizeNestedExtensionTables = (node: AdfNode, tableWidth: number): AdfNode =>
	resizeNestedExtensionTablesInternal(node, tableWidth).node;

export const getNestedExtensionTableWidth = (
	minWidth: number | undefined,
	extensionContainerWidth: number | undefined,
): number | undefined =>
	minWidth
		? Math.max(
				minWidth,
				(extensionContainerWidth ?? 0) - NESTED_EXTENSION_TABLE_INLINE_START_OFFSET,
			)
		: undefined;

export const useForgeExtensionContainerWidth = (
	enabled: boolean,
): ForgeExtensionContainerWidthResult => {
	const rendererContainerRef = React.useRef<HTMLDivElement>(null);
	const [extensionContainerWidth, setExtensionContainerWidth] = React.useState<number>();

	React.useEffect(() => {
		if (!enabled) {
			setExtensionContainerWidth(undefined);
			return;
		}

		const rendererContainer = rendererContainerRef.current;
		const extensionContainer = rendererContainer?.closest<HTMLElement>(
			FORGE_EXTENSION_CONTAINER_SELECTOR,
		);

		if (!rendererContainer || !extensionContainer) {
			return;
		}

		// Nested ADF can inherit a narrower saved width than the Forge host. Expand only when the
		// host currently offers more space, preserving the renderer width in every other case.
		const updateWidth = () => {
			const availableWidth = extensionContainer.getBoundingClientRect().width;
			const parentWidth = rendererContainer.parentElement?.getBoundingClientRect().width ?? 0;

			setExtensionContainerWidth(
				availableWidth > parentWidth + CONTAINER_WIDTH_TOLERANCE_PX
					? Math.round(availableWidth)
					: undefined,
			);
		};

		updateWidth();

		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const resizeObserver = new ResizeObserver(updateWidth);
		resizeObserver.observe(extensionContainer);
		if (rendererContainer.parentElement) {
			resizeObserver.observe(rendererContainer.parentElement);
		}

		return () => resizeObserver.disconnect();
	}, [enabled]);

	return { extensionContainerWidth, rendererContainerRef };
};

type ImportantStyles = Readonly<Record<string, string>>;

const setImportantStyles = (element: HTMLElement, styles: ImportantStyles): void => {
	const isCurrent = Object.entries(styles).every(
		([property, value]) =>
			element.style.getPropertyValue(property) === value &&
			element.style.getPropertyPriority(property) === 'important',
	);

	if (isCurrent) {
		return;
	}

	// cloneNode preserves attributes, including inline styles. Update the detached declaration so
	// the live element is mutated once without discarding unrelated Atlaskit styles.
	const nextStyle = (element.cloneNode(false) as HTMLElement).style;
	Object.entries(styles).forEach(([property, value]) => {
		nextStyle.setProperty(property, value, 'important');
	});
	element.style.cssText = nextStyle.cssText;
};

export const useNestedExtensionTableWidth = (
	rendererContainerRef: React.RefObject<HTMLDivElement>,
	tableWidth?: number,
): void => {
	React.useEffect(() => {
		if (!tableWidth) {
			return;
		}

		const rendererContainer = rendererContainerRef.current;
		if (!rendererContainer) {
			return;
		}

		const applyTableWidth = () => {
			const tableContainers =
				rendererContainer.querySelectorAll<HTMLElement>('.pm-table-container');
			const width = `${tableWidth}px`;

			tableContainers.forEach((tableContainer) => {
				const table = tableContainer.querySelector<HTMLElement>('table');
				const tableWrapper = tableContainer.querySelector<HTMLElement>('.pm-table-wrapper');
				const styleContainer = tableContainer.closest<HTMLElement>('[role="none"]');

				if (styleContainer) {
					setImportantStyles(styleContainer, {
						width,
						'max-width': 'none',
						'margin-inline-start': `${NESTED_EXTENSION_TABLE_INLINE_START_OFFSET}px`,
						'margin-inline-end': '0px',
					});
				}

				setImportantStyles(tableContainer, {
					width,
					'max-width': 'none',
				});
				if (tableWrapper) {
					setImportantStyles(tableWrapper, { width });
				}
				if (table) {
					setImportantStyles(table, { width });
				}
			});
		};

		applyTableWidth();
		const animationFrameId =
			typeof requestAnimationFrame === 'undefined'
				? undefined
				: requestAnimationFrame(applyTableWidth);
		const mutationObserver =
			typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(applyTableWidth);
		mutationObserver?.observe(rendererContainer, {
			attributeFilter: ['style'],
			attributes: true,
			subtree: true,
		});

		if (typeof ResizeObserver === 'undefined') {
			return () => {
				if (animationFrameId !== undefined) {
					cancelAnimationFrame(animationFrameId);
				}
				mutationObserver?.disconnect();
			};
		}

		const resizeObserver = new ResizeObserver(applyTableWidth);
		resizeObserver.observe(rendererContainer);

		return () => {
			if (animationFrameId !== undefined) {
				cancelAnimationFrame(animationFrameId);
			}
			mutationObserver?.disconnect();
			resizeObserver.disconnect();
		};
	}, [rendererContainerRef, tableWidth]);
};
